import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Filter, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import api from '../lib/api';

/**
 * Read-only viewer for the `admin_audit_log` collection.
 *
 * Surfaces who did what when, with optional diff snapshots. Built so that
 * the next time something disappears silently from the database we have an
 * unambiguous paper trail of which admin clicked which button.
 */

const ACTION_COLORS = {
  create: 'bg-forest/15 text-forest',
  update: 'bg-blue-50 text-blue-700',
  soft_delete: 'bg-amber-50 text-amber-700',
  restore: 'bg-emerald-50 text-emerald-700',
  purge: 'bg-red-50 text-red-700',
  publish: 'bg-forest/10 text-forest',
  unpublish: 'bg-stone text-charcoal/60',
  bulk_import: 'bg-purple-50 text-purple-700',
  bulk_link_accessable: 'bg-purple-50 text-purple-700',
};

function ActionPill({ action }) {
  const cls = ACTION_COLORS[action] || 'bg-stone text-charcoal/60';
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${cls}`}>
      {action.replace(/_/g, ' ')}
    </span>
  );
}

function DiffRow({ field, change }) {
  const before = change?.before;
  const after = change?.after;
  return (
    <div className="text-xs grid grid-cols-[120px_1fr_1fr] gap-2 py-1 border-b border-charcoal/5 last:border-b-0">
      <div className="font-mono text-charcoal/60 truncate">{field}</div>
      <div className="font-mono text-red-700/80 break-all">
        {before === undefined || before === null ? <span className="text-muted">∅</span> : String(typeof before === 'object' ? JSON.stringify(before) : before)}
      </div>
      <div className="font-mono text-forest break-all">
        {after === undefined || after === null ? <span className="text-muted">∅</span> : String(typeof after === 'object' ? JSON.stringify(after) : after)}
      </div>
    </div>
  );
}

function EntryRow({ entry }) {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.diff && Object.keys(entry.diff).length > 0;
  return (
    <div className="px-5 py-3 hover:bg-stone/30" data-testid={`audit-entry-${entry.id}`}>
      <button
        onClick={() => hasDiff && setOpen((o) => !o)}
        className="w-full text-left flex items-start gap-3"
        disabled={!hasDiff}
      >
        <div className="shrink-0 w-4 mt-1 text-charcoal/40">
          {hasDiff ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <ActionPill action={entry.action} />
            <span className="font-medium text-charcoal">{entry.resource_name || entry.resource_id}</span>
            <code className="text-[10px] text-muted">{entry.resource_type}/{entry.resource_id}</code>
          </div>
          <div className="text-xs text-muted mt-0.5">
            by <strong className="text-charcoal/80">{entry.actor_email}</strong>
            {' · '}
            {new Date(entry.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
            {hasDiff && <span> · {Object.keys(entry.diff).length} field{Object.keys(entry.diff).length === 1 ? '' : 's'} changed</span>}
          </div>
        </div>
      </button>
      {open && hasDiff && (
        <div className="mt-3 ml-7 bg-white border border-charcoal/10 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-charcoal/50 grid grid-cols-[120px_1fr_1fr] gap-2 mb-1">
            <div>Field</div>
            <div>Before</div>
            <div>After</div>
          </div>
          {Object.entries(entry.diff).map(([k, v]) => (
            <DiffRow key={k} field={k} change={v} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [actors, setActors] = useState([]);

  function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (actor) params.set('actor', actor);
    if (action) params.set('action', action);
    if (resourceType) params.set('resource_type', resourceType);
    params.set('limit', '100');
    api.get(`/admin/audit-log?${params.toString()}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }
  useEffect(load, [actor, action, resourceType]);

  useEffect(() => {
    api.get('/admin/audit-log/actors').then((r) => setActors(r.data.items || [])).catch(() => {});
  }, []);

  const actionsAvailable = useMemo(() => {
    const set = new Set(items.map((i) => i.action));
    return Array.from(set).sort();
  }, [items]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label text-heritage mb-2">Accountability</div>
          <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-3">
            <Activity size={28} /> Audit log
            <span className="text-charcoal/40 text-lg font-normal">({total})</span>
          </h1>
          <p className="text-sm text-muted mt-1">Every create / update / delete / restore action logged by an admin, newest first. Click an entry to see exactly which fields changed.</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="audit-reload"
          className="text-xs px-3 py-2 rounded-lg border border-charcoal/15 hover:border-charcoal/40 inline-flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3 text-sm" data-testid="audit-filters">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-charcoal/50"><Filter size={12} /> Filter</span>
        <select
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          data-testid="audit-filter-actor"
          className="bg-stone/40 border border-charcoal/10 rounded-md px-2 py-1.5 text-xs"
        >
          <option value="">All admins</option>
          {actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          data-testid="audit-filter-action"
          className="bg-stone/40 border border-charcoal/10 rounded-md px-2 py-1.5 text-xs"
        >
          <option value="">All actions</option>
          {['create', 'update', 'soft_delete', 'restore', 'purge', 'bulk_import', 'bulk_link_accessable'].map((a) => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select
          value={resourceType}
          onChange={(e) => setResourceType(e.target.value)}
          data-testid="audit-filter-resource"
          className="bg-stone/40 border border-charcoal/10 rounded-md px-2 py-1.5 text-xs"
        >
          <option value="">All resource types</option>
          <option value="attraction">attraction</option>
          <option value="event">event</option>
          <option value="member">member</option>
          <option value="package">package</option>
        </select>
        {(actor || action || resourceType) && (
          <button
            onClick={() => { setActor(''); setAction(''); setResourceType(''); }}
            className="text-xs text-muted hover:text-charcoal underline"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
        {items.length === 0 && !loading && (
          <div className="px-5 py-12 text-center text-sm text-muted" data-testid="audit-empty">
            No audit entries match these filters yet.
          </div>
        )}
        <div className="divide-y divide-charcoal/5">
          {items.map((e) => <EntryRow key={e.id} entry={e} />)}
        </div>
      </div>
    </div>
  );
}
